/* ═══════════════════════════════════════════════════════
   SwipeHome — Listings Module
   Likes screen, listing detail, gallery, claim flow,
   trust-layer loaders are in trust-layer.js
   ═══════════════════════════════════════════════════════ */

import { API, api } from './api.js';
import { goBack, navigate } from './router.js';
import {
  LEAD_TIER_HOT,
  LEAD_TIER_WARM,
  state,
} from './state.js';
import { cardMonthlyCashFlow, deriveCashToClose } from './investorCard.js';
import type { CardCashFlow } from './investorCard.js';
import { formatPrice } from '../../shared/format.js';
import { amenityRowHtml } from '../../shared/markup.js';
import type { AmenityLike } from '../../shared/markup.js';
import { NA, distanceTile } from '../../shared/tiles.js';
import {
  capRateLevel,
  cashOnCashLevel,
  dscrLevel,
  investmentScoreLevel,
  type MetricLevel,
} from '../../shared/underwriting.js';
import {
  escapeHtml,
  formatListingAddress,
  formatOpenHouseLabel,
  getAllMedia,
  getInitials,
  getMediaUrl,
  getUserName,
  imgFallback,
  ListControls,
  openModal,
  renderListingPhoto,
  showToast,
} from './ui.js';
import type { Listing } from '../../shared/types.js';
import { isHomecheckDemo, loadHomeCheck } from './trust-layer.js';
import { onLikesRefreshed, showMap } from './mapLazy.js';

// Investor pivot — investor-first detail sections (Deal Analysis,
// Financing Scenarios, Rent Comps, Connect with Providers). Gated
// inside renderInvestorDetailSections to no-op for non-investor users.
import { loadInvestorDetail, renderInvestorDealBlock } from './investorDetail.js';
import { mountProviderCtas, providerCtaSlot } from './providerCtas.js';

// ── Unused imports kept for inline onclick globals ─────────
void goBack;
void navigate;
void getInitials;
void getUserName;
void LEAD_TIER_WARM;
void imgFallback;
void openModal;

// Loosely typed helpers — HTML templates take mixed payloads.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyRecord = Record<string, any>;

// Extended state stash for scratch fields not on AppState.
interface ListingStash {
  _biFilter?: string;
  detailListing?: AnyRecord | null;
}

function listingStash(): ListingStash {
  return state as unknown as ListingStash;
}


// ═══════════════════════════════════════════════════════
// SCREEN 6: Liked Homes
// ═══════════════════════════════════════════════════════
let _likesControls: ListControls | null = null;

function renderLikes(): HTMLElement {
  const div = document.createElement('div');
  div.className = 'fade-in';
  div.style.display = 'flex';
  div.style.flexDirection = 'column';
  div.style.height = '100%';
  div.innerHTML = `
    <div class="screen-header">
      <h1>Watchlist</h1>
    </div>
    <div id="likes-map-view" class="feed-map-view" style="display:flex;--drawer-pct:58">
      <div id="likes-map-container" class="map-leaflet-container" style="position:absolute;top:0;left:0;right:0;height:calc(100% - var(--drawer-pct)*1%);background:var(--surface)"></div>
      <button id="likes-map-locate-btn" class="map-locate-btn" type="button" aria-label="Find my location" title="Find my location" onclick="locateMe()">
        <i data-feather="navigation" width="16"></i>
      </button>
      <div id="likes-map-empty-state" class="map-empty" style="display:none">
        <i data-feather="heart" width="32"></i>
        <div class="map-empty-message">No deals on your watchlist yet.</div>
      </div>
      <div id="likes-map-drawer" class="map-drawer medium" aria-label="Your liked homes">
        <div class="map-drawer-handle" id="likes-map-drawer-handle">
          <div class="map-drawer-grab"></div>
        </div>
        <div class="map-drawer-header">
          <span class="map-drawer-count" id="likes-map-drawer-count">Loading\u2026</span>
          <button class="likes-compare-toggle" id="likes-compare-toggle-btn" type="button" onclick="toggleCompareMode()">Compare</button>
        </div>
        <div class="map-drawer-scroll" id="likes-map-drawer-scroll">
          <div class="map-drawer-loading">
            <div class="spinner"></div>
          </div>
        </div>
      </div>
      <div id="likes-map-preview-sheet" class="map-preview-sheet" hidden></div>
    </div>
  `;

  setTimeout(() => {
    void showMap('likes');
  }, 50);
  return div;
}

// switchLikesView removed — Likes is always the combined map+drawer view.

// ═══════════════════════════════════════════════════════
// Tier 3 — Compare homes side-by-side
// ═══════════════════════════════════════════════════════
const MAX_COMPARE = 3;

function toggleCompareMode(): void {
  state.compareMode = !state.compareMode;
  if (!state.compareMode) state.compareListingIds = [];
  // Re-render the likes screen to show/hide checkboxes + toolbar
  const wrapper = document.getElementById('likes-map-view');
  if (wrapper) wrapper.classList.toggle('compare-mode', state.compareMode);
  updateCompareToolbar();
  // Trigger a drawer re-render so cards show checkbox state
  const scroll = document.getElementById('likes-map-drawer-scroll');
  if (scroll && (window as unknown as { onLikesRefreshed?: () => void }).onLikesRefreshed) {
    (window as unknown as { onLikesRefreshed: () => void }).onLikesRefreshed();
  } else {
    onLikesRefreshed();
  }
  // Update button text
  const btn = document.getElementById('likes-compare-toggle-btn');
  if (btn) btn.textContent = state.compareMode ? 'Cancel' : 'Compare';
}

function toggleCompareSelection(listingId: string): void {
  const idx = state.compareListingIds.indexOf(listingId);
  if (idx >= 0) {
    state.compareListingIds.splice(idx, 1);
  } else {
    if (state.compareListingIds.length >= MAX_COMPARE) {
      showToast(`Max ${MAX_COMPARE} homes at a time`, 'info');
      return;
    }
    state.compareListingIds.push(listingId);
  }
  updateCompareCheckboxes();
  updateCompareToolbar();
}

function updateCompareCheckboxes(): void {
  document.querySelectorAll<HTMLElement>('.map-card[data-listing-id]').forEach((card) => {
    const id = card.dataset.listingId;
    const selected = id && state.compareListingIds.includes(id);
    card.classList.toggle('compare-selected', !!selected);
  });
}

function updateCompareToolbar(): void {
  let toolbar = document.getElementById('compare-toolbar');
  if (!state.compareMode || state.compareListingIds.length === 0) {
    toolbar?.remove();
    return;
  }
  if (!toolbar) {
    toolbar = document.createElement('div');
    toolbar.id = 'compare-toolbar';
    toolbar.className = 'compare-toolbar';
    document.body.appendChild(toolbar);
  }
  const n = state.compareListingIds.length;
  toolbar.innerHTML = `
    <span class="compare-toolbar-count">${n} selected</span>
    <button class="compare-toolbar-go" type="button" ${n < 2 ? 'disabled' : ''} onclick="openCompareScreen()">
      Compare ${n >= 2 ? `(${n})` : ''}
    </button>
  `;
}

function openCompareScreen(): void {
  if (state.compareListingIds.length < 2) return;
  // Remove the toolbar from body so it doesn't linger on the compare screen
  document.getElementById('compare-toolbar')?.remove();
  navigate('compare');
}

/**
 * Helper used by renderDrawerContent (mapView.ts) via the global
 * `shouldShowCompareCheckbox` — exported here and imported by the
 * drawer renderer so Discover cards don't accidentally get the
 * compare UI.
 */
export function isCompareSelected(listingId: string): boolean {
  return state.compareMode && state.compareListingIds.includes(listingId);
}

export function isCompareModeActive(): boolean {
  return state.compareMode;
}

/**
 * Render the compare screen — columns per selected listing with
 * price, address, beds/baths/sqft, year, HOA, Photo-Truth score,
 * and estimated True Cost monthly. The data comes from the Likes
 * cache; if a listing is missing from the cache (shouldn't happen
 * but defensive), that column shows a stub.
 */
/**
 * Per-listing data bundle used by the compare screen.
 */
interface CompareListing extends Record<string, unknown> {
  listing: AnyRecord;
}

/** Row types in the compare matrix. Drives winner computation. */
type CompareWinnerMode = 'lowest' | 'highest' | null;

interface CompareRow {
  label: string;
  /** Return a numeric value for winner computation (or null to skip). */
  getNumeric?: (b: CompareListing) => number | null;
  /** Render the cell's display string (may include HTML). */
  getDisplay: (b: CompareListing) => string;
  winnerMode?: CompareWinnerMode;
}

interface CompareSection {
  label: string;
  rows: CompareRow[];
}

function getPricePerSqft(b: CompareListing): number | null {
  const p = typeof b.listing.price === 'number' ? b.listing.price : null;
  const s = typeof b.listing.sqft === 'number' ? b.listing.sqft : null;
  if (!p || !s) return null;
  return Math.round(p / s);
}

function getDaysOnMarket(b: CompareListing): number | null {
  const ts = b.listing.published_at as string | null | undefined;
  if (!ts) return null;
  const diff = Date.now() - new Date(ts).getTime();
  return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
}

/** Monthly cash flow — the SAME resolution the swipe card uses
 *  (investorCard cardMonthlyCashFlow): the analyzer's `monthly_cash_flow`
 *  when the payload has it — the compare fetch (/listings/{id}) doesn't,
 *  so it is read from the Watchlist (/swipes/likes) cache in state.likes —
 *  else the cap_rate × price − debt-service derivation, tagged "est". */
function getCompareCashFlowInfo(b: CompareListing): CardCashFlow {
  const l = b.listing;
  const liked = state.likes.find((x) => x.id === l.id);
  const payload = typeof l.monthly_cash_flow === 'number' ? l.monthly_cash_flow : liked?.monthly_cash_flow;
  return cardMonthlyCashFlow({
    price: l.price as number,
    cap_rate: (l.cap_rate as number | null | undefined) ?? null,
    monthly_cash_flow: payload,
  });
}
function getCompareCashFlow(b: CompareListing): number | null {
  return getCompareCashFlowInfo(b).value;
}

/** Cash to close — price × (down % + 3 % closing) under the user's
 *  resolved loan terms, i.e. the analyzer's cash_invested with rehab 0
 *  (investment_analyzer: down + closing_costs_pct + rehab). Est. */
function getCompareCashToClose(b: CompareListing): number | null {
  return deriveCashToClose(b.listing as { price: number });
}

const EST_TAG = ' <span class="inv-est">est</span>';

// Wishlist #5: investor-spec'd Compare. Investment section leads because
// it's the primary lens for an investor weighing 2-3 saved deals. The
// metrics flow from the /swipes/likes payload (post-fix #4 they include
// cap_rate, cash_on_cash_return, dscr_ratio, gross_rent_multiplier,
// estimated_rent, investment_score). Winner highlighting picks the best
// of the compared set per metric \u2014 green chip for the winner, neutral
// for the rest. Color thresholds also flag absolute pass/fail levels:
// cap rate, CoC, DSCR, and score use the canonical levels from
// shared/underwriting.ts so a metric never shows a different color here
// than on the swipe card or detail page.
// Winner highlighting still picks the best of the compared set per metric —
// green chip for the winner, neutral for the rest.
function _levelCss(level: MetricLevel): string {
  switch (level) {
    case 'green': return 'var(--success)';
    case 'yellow': return 'var(--warning)';
    case 'red': return 'var(--danger)';
    default: return 'var(--text-muted)';
  }
}
function _capColor(v: number | null): string { return _levelCss(capRateLevel(v)); }
function _cocColor(v: number | null): string { return _levelCss(cashOnCashLevel(v)); }
function _dscrColor(v: number | null): string { return _levelCss(dscrLevel(v)); }
function _scoreColor(v: number | null): string { return _levelCss(investmentScoreLevel(v)); }
function _cfColor(v: number | null): string {
  if (v == null) return 'var(--text-muted)';
  if (v >= 0) return 'var(--success)';
  return 'var(--danger)';
}

const COMPARE_SECTIONS: CompareSection[] = [
  {
    label: 'Investment',
    rows: [
      {
        label: 'Investment score',
        getNumeric: (b) => (typeof b.listing.investment_score === 'number' ? b.listing.investment_score : null),
        getDisplay: (b) => {
          const v = typeof b.listing.investment_score === 'number' ? b.listing.investment_score : null;
          if (v == null) return '\u2014';
          return `<span style="font-weight:800;color:${_scoreColor(v)}">${v}</span>`;
        },
        winnerMode: 'highest',
      },
      {
        label: 'Cap rate',
        getNumeric: (b) => (typeof b.listing.cap_rate === 'number' ? b.listing.cap_rate : null),
        getDisplay: (b) => {
          const v = typeof b.listing.cap_rate === 'number' ? b.listing.cap_rate : null;
          if (v == null) return NA;
          return `<span style="font-weight:700;color:${_capColor(v)}">${v.toFixed(2)}%</span>`;
        },
        winnerMode: 'highest',
      },
      {
        label: 'Cash-on-cash',
        getNumeric: (b) => (typeof b.listing.cash_on_cash_return === 'number' ? b.listing.cash_on_cash_return : null),
        getDisplay: (b) => {
          const v = typeof b.listing.cash_on_cash_return === 'number' ? b.listing.cash_on_cash_return : null;
          if (v == null) return NA;
          return `<span style="font-weight:700;color:${_cocColor(v)}">${v.toFixed(2)}%</span>`;
        },
        winnerMode: 'highest',
      },
      {
        label: 'DSCR',
        getNumeric: (b) => (typeof b.listing.dscr_ratio === 'number' ? b.listing.dscr_ratio : null),
        getDisplay: (b) => {
          const v = typeof b.listing.dscr_ratio === 'number' ? b.listing.dscr_ratio : null;
          if (v == null) return NA;
          return `<span style="font-weight:700;color:${_dscrColor(v)}">${v.toFixed(2)}</span>`;
        },
        winnerMode: 'highest',
      },
      {
        label: 'Est. rent',
        getNumeric: (b) => (typeof b.listing.estimated_rent === 'number' ? b.listing.estimated_rent : null),
        getDisplay: (b) => {
          const v = typeof b.listing.estimated_rent === 'number' ? b.listing.estimated_rent : null;
          if (v == null) return '\u2014';
          return `$${Math.round(v).toLocaleString()}/mo`;
        },
        winnerMode: 'highest',
      },
      {
        label: 'Monthly cash flow',
        // Same figure as the swipe card (analyzer payload, else cap_rate ×
        // price − the user's own debt service) — never a second model
        // with fixed 20% / 6.5%. Only the derived fallback is tagged "est".
        getNumeric: getCompareCashFlow,
        getDisplay: (b) => {
          const { value: cf, estimated } = getCompareCashFlowInfo(b);
          if (cf == null) return '\u2014';
          return `<span style="font-weight:700;color:${_cfColor(cf)}">${cf >= 0 ? '+' : '\u2212'}$${Math.abs(Math.round(cf)).toLocaleString()}/mo</span>${estimated ? EST_TAG : ''}`;
        },
        winnerMode: 'highest',
      },
      {
        label: 'Gross rent multiplier',
        getNumeric: (b) => (typeof b.listing.gross_rent_multiplier === 'number' ? b.listing.gross_rent_multiplier : null),
        getDisplay: (b) => {
          const v = typeof b.listing.gross_rent_multiplier === 'number' ? b.listing.gross_rent_multiplier : null;
          return v != null ? v.toFixed(2) : NA;
        },
        winnerMode: 'lowest',
      },
    ],
  },
  {
    label: 'Price',
    rows: [
      {
        label: 'Price',
        getNumeric: (b) => (typeof b.listing.price === 'number' ? b.listing.price : null),
        getDisplay: (b) => (typeof b.listing.price === 'number' ? formatPrice(b.listing.price) : '\u2014'),
        winnerMode: 'lowest',
      },
      {
        label: '$ / sqft',
        getNumeric: getPricePerSqft,
        getDisplay: (b) => {
          const v = getPricePerSqft(b);
          return v != null ? `$${v.toLocaleString()}` : '\u2014';
        },
        winnerMode: 'lowest',
      },
      {
        label: 'HOA',
        // Tri-state: hoa_fee known → fee; hoa === true → has HOA (fee
        // unknown); hoa === false → confirmed none; null/undefined →
        // unknown (imported listings where the source never said).
        getNumeric: (b) => (b.listing.hoa_fee as number | null) ?? (b.listing.hoa === true ? 0 : null),
        getDisplay: (b) => {
          if (b.listing.hoa_fee) return `$${(b.listing.hoa_fee as number).toLocaleString()}/mo`;
          return b.listing.hoa === true ? 'Yes (fee unknown)' : b.listing.hoa === false ? 'None' : 'Unknown';
        },
        winnerMode: 'lowest',
      },
      // "Est. monthly" / "Est. property tax" / "Est. insurance" rows were
      // removed: the /swipes/likes payload carries no tax or insurance
      // figures, and the old 1.2% / 0.35% price-ratio guesses contradicted
      // the detail page's analyzer line items.
      {
        label: 'Cash to close',
        getNumeric: getCompareCashToClose,
        getDisplay: (b) => {
          const v = getCompareCashToClose(b);
          return v != null ? `$${v.toLocaleString()}${EST_TAG}` : '\u2014';
        },
        winnerMode: 'lowest',
      },
      {
        label: 'Days on market',
        getNumeric: getDaysOnMarket,
        getDisplay: (b) => {
          const v = getDaysOnMarket(b);
          if (v == null) return '\u2014';
          return v === 1 ? '1 day' : `${v} days`;
        },
        winnerMode: 'lowest',
      },
    ],
  },
  {
    label: 'Home',
    rows: [
      {
        label: 'Beds',
        getNumeric: (b) => (typeof b.listing.beds === 'number' ? b.listing.beds : null),
        getDisplay: (b) => (b.listing.beds != null ? String(b.listing.beds) : '\u2014'),
        winnerMode: 'highest',
      },
      {
        label: 'Baths',
        getNumeric: (b) => (typeof b.listing.baths === 'number' ? b.listing.baths : null),
        getDisplay: (b) => (b.listing.baths != null ? String(b.listing.baths) : '\u2014'),
        winnerMode: 'highest',
      },
      {
        label: 'Sqft',
        getNumeric: (b) => (typeof b.listing.sqft === 'number' ? b.listing.sqft : null),
        getDisplay: (b) => {
          const v = b.listing.sqft;
          return typeof v === 'number' ? v.toLocaleString() : '\u2014';
        },
        winnerMode: 'highest',
      },
      {
        label: 'Lot size',
        getNumeric: (b) => (typeof b.listing.lot_sqft === 'number' ? b.listing.lot_sqft : null),
        getDisplay: (b) => {
          const v = b.listing.lot_sqft;
          return typeof v === 'number' ? `${v.toLocaleString()} sf` : '\u2014';
        },
        winnerMode: 'highest',
      },
      {
        label: 'Year built',
        getNumeric: (b) => (typeof b.listing.year_built === 'number' ? b.listing.year_built : null),
        getDisplay: (b) => (b.listing.year_built != null ? String(b.listing.year_built) : '\u2014'),
        winnerMode: 'highest',
      },
      {
        label: 'Property type',
        getDisplay: (b) => {
          const t = b.listing.property_type;
          return typeof t === 'string' ? t.replace(/_/g, ' ') : '\u2014';
        },
      },
    ],
  },
  {
    label: 'Condition',
    rows: [
      {
        label: 'Reality Score',
        getNumeric: (b) => (typeof b.listing.reality_score === 'number' ? b.listing.reality_score : null),
        getDisplay: (b) => {
          const s = b.listing.reality_score;
          if (typeof s !== 'number') return '<span class="cmp-trust none">\u2014</span>';
          if (s >= 80) return `<span class="cmp-trust verified">\u2713 ${s}</span>`;
          if (s >= 50) return `<span class="cmp-trust enhanced">! ${s}</span>`;
          return `<span class="cmp-trust none">${s}</span>`;
        },
        winnerMode: 'highest',
      },
      {
        label: 'Turnkey',
        getDisplay: (b) => {
          if (b.listing.is_turnkey === true) return '<span class="cmp-flag yes">Yes</span>';
          if (b.listing.is_turnkey === false) return '<span class="cmp-flag no">No</span>';
          return '\u2014';
        },
      },
      {
        label: 'Needs rehab',
        getDisplay: (b) => {
          if (b.listing.needs_rehab === true) return '<span class="cmp-flag warn">Yes</span>';
          if (b.listing.needs_rehab === false) return '<span class="cmp-flag yes">No</span>';
          return '\u2014';
        },
      },
    ],
  },
  {
    label: 'Market',
    rows: [
      {
        label: 'Open house',
        getDisplay: (b) => {
          const oh = b.listing.next_open_house as {
            event_date: string;
            start_time: string | null;
            end_time: string | null;
          } | null | undefined;
          if (!oh) return '<span class="cmp-flag no">None scheduled</span>';
          const label = formatOpenHouseLabel(oh);
          return `<span class="cmp-flag yes">${escapeHtml(label)}</span>`;
        },
      },
      {
        label: 'Status',
        getDisplay: (b) => {
          const s = b.listing.status;
          if (s === 'active') return '<span class="cmp-status active">Active</span>';
          if (s === 'pending') return '<span class="cmp-status pending">Pending</span>';
          if (s === 'sold') return '<span class="cmp-status sold">Sold</span>';
          if (s === 'inactive') return '<span class="cmp-status sold">Inactive</span>';
          return typeof s === 'string' ? s : '\u2014';
        },
      },
      {
        label: 'Likes',
        getNumeric: (b) => (typeof b.listing.like_count === 'number' ? b.listing.like_count : null),
        getDisplay: (b) => (typeof b.listing.like_count === 'number' ? String(b.listing.like_count) : '\u2014'),
        winnerMode: 'highest',
      },
      {
        label: 'Listing agent',
        getDisplay: (b) => {
          const n = b.listing.listing_agent_name;
          const brk = b.listing.listing_agent_brokerage;
          if (typeof n === 'string' && n) {
            return brk ? `${escapeHtml(n)}<br><span class="cmp-sub">${escapeHtml(brk as string)}</span>` : escapeHtml(n);
          }
          return '\u2014';
        },
      },
    ],
  },
];

/**
 * Given the value of a row across all compared listings, return the
 * indices that "win" per winnerMode. Ties all win together so the
 * UI doesn't arbitrarily pick one.
 */
function computeWinnerIndices(values: (number | null)[], mode: CompareWinnerMode): Set<number> {
  const winners = new Set<number>();
  if (!mode) return winners;
  const valid = values.filter((v): v is number => v != null);
  if (valid.length < 2) return winners; // only one data point — no winner
  const best = mode === 'lowest' ? Math.min(...valid) : Math.max(...valid);
  values.forEach((v, i) => { if (v === best) winners.add(i); });
  return winners;
}

function renderCompare(): HTMLElement {
  const div = document.createElement('div');
  div.className = 'fade-in compare-screen';

  if (state.compareListingIds.length < 2) {
    div.innerHTML = `
      <div class="screen-header">
        <button class="compare-back-btn" onclick="goBack()">
          <i data-feather="arrow-left" width="18"></i>
        </button>
        <h1>Compare</h1>
      </div>
      <div class="empty-state" style="padding:40px 16px">
        <i data-feather="copy" width="48"></i>
        <h3>Nothing to compare</h3>
        <p>Pick 2 or 3 saved homes to see them side by side.</p>
      </div>
    `;
    setTimeout(() => window.feather?.replace(), 10);
    return div;
  }

  // Fetch each listing in parallel for the compare matrix.
  Promise.all(
    state.compareListingIds.map(async (id): Promise<CompareListing | null> => {
      try {
        const data = await api<AnyRecord>('GET', `/listings/${id}`);
        const listing = ((data.listing as AnyRecord) || (data as AnyRecord));
        return { listing };
      } catch {
        return null;
      }
    }),
  ).then((results) => {
    const bundles = results.filter((b): b is CompareListing => b !== null);
    if (bundles.length === 0) {
      div.innerHTML = `
        <div class="screen-header">
          <button class="compare-back-btn" onclick="goBack()">
            <i data-feather="arrow-left" width="18"></i>
          </button>
          <h1>Compare</h1>
        </div>
        <div class="empty-state" style="padding:40px 16px">
          <p>Couldn't load those listings. Try again.</p>
        </div>
      `;
      window.feather?.replace();
      return;
    }
    renderCompareColumns(div, bundles);
  });

  div.innerHTML = `
    <div class="screen-header">
      <button class="compare-back-btn" onclick="goBack()">
        <i data-feather="arrow-left" width="18"></i>
      </button>
      <h1>Compare</h1>
    </div>
    <div class="compare-loading">
      <div class="spinner"></div>
      <p>Loading homes...</p>
    </div>
  `;
  setTimeout(() => window.feather?.replace(), 10);
  return div;
}

function renderCompareColumns(div: HTMLElement, bundles: CompareListing[]): void {
  // Column headers — photo, price, address — unchanged from v1.
  const headerCells = bundles
    .map((b) => {
      const l = b.listing;
      const photo = getMediaUrl(l as unknown as Parameters<typeof getMediaUrl>[0]);
      const addr = formatListingAddress(l);
      const price = typeof l.price === 'number' ? formatPrice(l.price) : '\u2014';
      const imgHtml = photo
        ? `<img class="cmp-photo" src="${escapeHtml(photo)}" alt="${escapeHtml(l.address_line1 as string || '')}" loading="lazy" onerror="imgFallback(this)">`
        : '<div class="cmp-photo"></div>';
      return `
        <div class="cmp-col-header" onclick="openDetail('${escapeHtml(l.id as string)}')">
          ${imgHtml}
          <div class="cmp-price">${escapeHtml(price)}</div>
          <div class="cmp-addr">${escapeHtml(addr)}</div>
        </div>
      `;
    })
    .join('');

  // Build the section/row matrix with winner computation per row.
  const sectionsHtml = COMPARE_SECTIONS.map((section) => {
    const rowsHtml = section.rows.map((row) => {
      const numericValues = bundles.map((b) =>
        row.getNumeric ? row.getNumeric(b) : null,
      );
      const winners = computeWinnerIndices(numericValues, row.winnerMode ?? null);

      const cells = bundles
        .map((b, i) => {
          const isWinner = winners.has(i);
          return `
            <div class="cmp-cell ${isWinner ? 'cmp-cell-winner' : ''}">
              ${isWinner ? '<span class="cmp-winner-star" aria-label="Best in this row">\u2605</span>' : ''}
              <span class="cmp-cell-val">${row.getDisplay(b)}</span>
            </div>
          `;
        })
        .join('');

      return `
        <div class="cmp-row">
          <div class="cmp-row-label">${escapeHtml(row.label)}</div>
          <div class="cmp-row-cells" style="grid-template-columns:repeat(${bundles.length}, 1fr)">
            ${cells}
          </div>
        </div>
      `;
    }).join('');

    return `
      <div class="cmp-section">
        <div class="cmp-section-label">${escapeHtml(section.label)}</div>
        ${rowsHtml}
      </div>
    `;
  }).join('');

  div.innerHTML = `
    <div class="screen-header">
      <button class="compare-back-btn" onclick="goBack()">
        <i data-feather="arrow-left" width="18"></i>
      </button>
      <h1>Compare</h1>
    </div>
    <div class="compare-scroll">
      <div class="compare-matrix" style="--cmp-cols:${bundles.length}">
        <div class="cmp-headers" style="grid-template-columns:repeat(${bundles.length}, 1fr)">
          ${headerCells}
        </div>
        ${sectionsHtml}
      </div>
    </div>
  `;
  if (window.feather) window.feather.replace();
}

// ═══════════════════════════════════════════════════════

async function loadLikes(params?: AnyRecord): Promise<void> {
  const container = document.getElementById('likes-list');
  if (!container) return;

  try {
    const qs = params ? '?' + new URLSearchParams(params as Record<string, string>).toString() : '';
    // Bucket E cleanup: the parallel /offers fetch (used to overlay an "offer
    // sent" badge on liked homes) was removed with the offers backend.
    const data = await api<AnyRecord>('GET', '/swipes/likes' + qs);
    const items = (data.items as AnyRecord[]) || (data.likes as AnyRecord[]) || (Array.isArray(data) ? (data as AnyRecord[]) : []);

    if (items.length === 0 && (!params || params.page === 1)) {
      const isFiltered = params && (params.search || params.min_beds || params.min_price || params.max_price);
      container.innerHTML = isFiltered ? `
        <div class="list-empty-search">
          <div class="list-empty-search-icon">\ud83d\udd0d</div>
          <div class="list-empty-search-title">No results found</div>
          <div class="list-empty-search-subtitle">Try adjusting your search or filters</div>
        </div>
      ` : `
        <div class="empty-state">
          <i data-feather="heart" width="48"></i>
          <h3>No saved homes yet</h3>
          <p>Homes you save will appear here with full verification details.</p>
          <button class="btn btn-primary mt-16" onclick="navigate('feed')">Browse Verified Listings</button>
        </div>
      `;
    } else {
      container.innerHTML = `
        <div class="listing-list">
          ${items.map((item: AnyRecord) => {
            const listing = (item.listing as AnyRecord) || item;
            const img = getMediaUrl(listing);
            return `
              <div class="listing-item" onclick="openDetail('${listing.id}')">
                ${renderListingPhoto(img, 'listing-item-image', 'Home photo')}
                <div class="listing-item-info">
                  <div class="listing-item-price">${formatPrice(listing.price)}</div>
                  <div class="listing-item-address">${escapeHtml(formatListingAddress(listing))}</div>
                  <div class="listing-item-meta">
                    <span>${listing.beds ? listing.beds : '\u2014'} bd</span>
                    <span>${listing.baths != null ? listing.baths : '\u2014'} ba</span>
                    ${listing.sqft ? `<span>${listing.sqft.toLocaleString()} sf</span>` : ''}
                    ${listing.year_built ? `<span>${listing.year_built}</span>` : ''}
                    ${listing.property_type ? `<span>${listing.property_type.replace(/_/g, ' ')}</span>` : ''}
                  </div>
                </div>
                ${state.role === 'buyer' ? `<div style="display:flex;flex-direction:column;gap:4px;flex-shrink:0;margin-left:8px">
                  <button onclick="event.stopPropagation(); openListingThread('${listing.id}')" title="Message Agent" style="background:var(--surface-alt);border:1px solid var(--border);border-radius:8px;padding:6px 8px;cursor:pointer;color:var(--text-secondary);flex-shrink:0"><i data-feather="message-square" width="12"></i></button>
                </div>` : ''}
              </div>
            `;
          }).join('')}
        </div>
      `;
    }
    if (window.feather) window.feather.replace();
    if (data.pagination && _likesControls) _likesControls.updatePagination(data.pagination);
    // Let the Likes map pick up the refreshed data if it's currently visible.
    onLikesRefreshed();
  } catch (err: any) {
    container.innerHTML = `<div class="empty-state"><p class="text-danger">${escapeHtml(err.message)}</p></div>`;
  }
}

// ═══════════════════════════════════════════════════════
// Tier 3 — Recently viewed carousel
// ═══════════════════════════════════════════════════════

const RECENTLY_VIEWED_KEY = 'swipehome_recently_viewed';
const RECENTLY_VIEWED_CAP = 20;

interface RecentlyViewedItem {
  id: string;
  price: number | null;
  photo: string;
  address_line1: string;
  city: string;
  state: string;
  beds: number | null;
  baths: number | null;
  sqft: number | null;
  viewed_at: number;
}

function trackRecentlyViewed(listing: AnyRecord): void {
  if (!listing || !listing.id) return;
  try {
    const existing: RecentlyViewedItem[] = JSON.parse(
      localStorage.getItem(RECENTLY_VIEWED_KEY) || '[]',
    );
    // Remove any prior entry for this id so the new view jumps to front
    const filtered = existing.filter((e) => e.id !== listing.id);
    const photo = getMediaUrl(listing as unknown as Parameters<typeof getMediaUrl>[0]);
    const entry: RecentlyViewedItem = {
      id: String(listing.id),
      price: typeof listing.price === 'number' ? listing.price : null,
      photo: photo || '',
      address_line1: String(listing.address_line1 || ''),
      city: String(listing.city || ''),
      state: String(listing.state || ''),
      beds: typeof listing.beds === 'number' ? listing.beds : null,
      baths: typeof listing.baths === 'number' ? listing.baths : null,
      sqft: typeof listing.sqft === 'number' ? listing.sqft : null,
      viewed_at: Date.now(),
    };
    const next = [entry, ...filtered].slice(0, RECENTLY_VIEWED_CAP);
    localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(next));
  } catch {
    /* localStorage full or unavailable — silent */
  }
}

function getRecentlyViewed(): RecentlyViewedItem[] {
  try {
    const raw = localStorage.getItem(RECENTLY_VIEWED_KEY);
    if (!raw) return [];
    const parsed: RecentlyViewedItem[] = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Render the horizontal "Recently viewed" carousel. Returns HTML
 * string for the feed screen to inject above the feed tabs. Empty
 * string when the user has no history (first-time users).
 */
function renderRecentlyViewedRow(opts?: { bare?: boolean }): string {
  const items = getRecentlyViewed();
  if (items.length === 0) return '';

  const cards = items
    .map((it) => {
      const priceLabel = it.price ? formatPrice(it.price) : '';
      const addr = formatListingAddress(it);
      const photoHtml = it.photo
        ? `<img class="rv-card-photo" src="${escapeHtml(it.photo)}" alt="${escapeHtml(it.address_line1)}" loading="lazy" onerror="imgFallback(this)">`
        : '<div class="rv-card-photo"></div>';
      return `
        <div class="rv-card" onclick="openDetail('${escapeHtml(it.id)}')">
          ${photoHtml}
          <div class="rv-card-price">${escapeHtml(priceLabel)}</div>
          <div class="rv-card-addr">${escapeHtml(addr)}</div>
        </div>
      `;
    })
    .join('');

  if (opts?.bare) {
    return `
      <div class="rv-section rv-section-bare">
        <div class="rv-scroll">${cards}</div>
        <button class="rv-header-clear rv-clear-footer" type="button" onclick="clearRecentlyViewed()">Clear recently viewed</button>
      </div>
    `;
  }

  return `
    <div class="rv-section">
      <div class="rv-header">
        <span class="rv-header-title">Recently viewed</span>
        <button class="rv-header-clear" type="button" onclick="clearRecentlyViewed()">Clear</button>
      </div>
      <div class="rv-scroll">${cards}</div>
    </div>
  `;
}

function clearRecentlyViewed(): void {
  try {
    localStorage.removeItem(RECENTLY_VIEWED_KEY);
  } catch {
    /* ignore */
  }
  // Repaint the Profile-tab slot so the row disappears in place.
  const slot = document.getElementById('profile-recently-viewed-slot');
  if (slot) slot.innerHTML = `<div class="profile-empty-row" style="padding:8px 0;color:var(--text-muted);font-size:13px">Nothing here yet — listings you peek at will show up here.</div>`;
}


// ═══════════════════════════════════════════════════════

async function openDetail(listingId: string): Promise<void> {
  try {
    const data = await api<AnyRecord>('GET', `/listings/${listingId}`);
    state.selectedListing = ((data.listing as Listing) || (data as unknown as Listing));
    navigate('detail');
  } catch (err: any) {
    showToast(err.message, 'error');
  }
}

/**
 * Tier 2 — share a listing via the native Web Share API, falling
 * back to clipboard copy. The URL embeds the listing id in a hash
 * fragment so the receiving client can route into the detail view.
 * The backend's /api/public/listings/{id} endpoint serves a reduced
 * payload for any link preview service that fetches the URL.
 */
async function shareListing(listingId: string): Promise<void> {
  const listing = state.selectedListing as unknown as AnyRecord | null;
  const url = `${window.location.origin}/#listing=${listingId}`;
  const title = listing && listing.address_line1
    ? formatListingAddress(listing)
    : 'A home on SwipeHome';
  const text = listing && listing.price
    ? `${formatPrice(listing.price)} \u00B7 ${title}`
    : title;

  // Native Web Share API — mobile browsers + Safari/Edge desktop
  const nav = navigator as Navigator & { share?: (data: ShareData) => Promise<void> };
  if (nav.share) {
    try {
      await nav.share({ title, text, url });
      return;
    } catch (err) {
      // AbortError when the user cancels the share sheet — silent.
      // Other errors fall through to clipboard fallback below.
      if ((err as Error).name === 'AbortError') return;
    }
  }

  // Fallback: copy the URL to the clipboard
  try {
    await navigator.clipboard.writeText(url);
    showToast('Link copied to clipboard', 'success');
  } catch {
    // Clipboard API blocked (no HTTPS, permissions denied) — last
    // resort: show the URL in a toast so the user can copy manually.
    showToast(`Share this link: ${url}`, 'info');
  }
}


// ═══════════════════════════════════════════════════════
// SCREEN 7: Listing Detail
// ═══════════════════════════════════════════════════════

// Major zones for the sticky sub-nav. Due Diligence starts at Photo-Truth,
// the first verification section after the deal numbers.
const DETAIL_ZONES: { label: string; target: string }[] = [
  { label: 'Deal Analysis', target: 'inv-deal-analysis-section' },
  { label: 'Due Diligence', target: 'photo-truth-section' },
  { label: 'Neighborhood', target: 'neighborhood-section' },
];

/** Wire the sticky sub-nav: smooth-scroll #screen-container to a zone
 *  (offset by the sticky bar's height) and highlight the zone in view. */
function initDetailSubnav(root: HTMLElement): void {
  const scroller = document.getElementById('screen-container');
  const nav = root.querySelector<HTMLElement>('.detail-subnav');
  if (!scroller || !nav) return;
  const links = Array.from(nav.querySelectorAll<HTMLButtonElement>('.detail-subnav-link'));

  // Distance from the top of the scroller's content to an element.
  const topOf = (el: HTMLElement) =>
    el.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;

  nav.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.detail-subnav-link');
    const target = btn?.dataset.target ? document.getElementById(btn.dataset.target) : null;
    if (!target) return;
    scroller.scrollTo({ top: Math.max(0, topOf(target) - nav.offsetHeight - 8), behavior: 'smooth' });
  });

  const onScroll = () => {
    // The listener lives on the shared scroller — drop it once the detail
    // screen is gone.
    if (!nav.isConnected) {
      scroller.removeEventListener('scroll', onScroll);
      return;
    }
    const line = nav.getBoundingClientRect().bottom + 24;
    // At the very bottom the last zone may never reach the line (the page
    // runs out first), so any zone whose top is on screen counts.
    const atBottom = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 2;
    const limit = atBottom ? scroller.getBoundingClientRect().bottom : line;
    let active = -1;
    DETAIL_ZONES.forEach((z, i) => {
      const el = document.getElementById(z.target);
      if (el && el.getBoundingClientRect().top <= limit) active = i;
    });
    links.forEach((l, i) => l.classList.toggle('active', i === active));
  };
  scroller.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

function renderDetail(): HTMLElement {
  const listing = state.selectedListing as unknown as AnyRecord | null;
  if (!listing) { goBack(); return document.createElement('div'); }

  // Tier 3 — track "recently viewed" for the horizontal carousel
  // on the feed. Catches every entry point (swipe Info, Likes tap,
  // map detail CTA, share deep link) because all of them land here.
  trackRecentlyViewed(listing);

  const media = getAllMedia(listing as unknown as Parameters<typeof getAllMedia>[0]);
  const div = document.createElement('div');
  div.className = 'fade-in';

  div.innerHTML = `
    <div class="detail-gallery" id="detail-gallery">
      ${media.length > 0 ? `
        <img class="img-loading detail-img-tappable"
             src="${escapeHtml(media[0].url as string)}"
             alt="Home photo"
             id="detail-img"
             data-index="0"
             loading="eager"
             onclick="openPhotoGalleryForListing(parseInt(this.dataset.index || '0', 10))"
             onload="this.classList.add('loaded')"
             onerror="imgFallback(this)">
        ${media.length > 1 ? `
          <button class="detail-gallery-nav prev" onclick="event.stopPropagation(); galleryNav(-1)"><i data-feather="chevron-left" width="18"></i></button>
          <button class="detail-gallery-nav next" onclick="event.stopPropagation(); galleryNav(1)"><i data-feather="chevron-right" width="18"></i></button>
          <div class="image-dots">
            ${media.map((_, i) => `<div class="image-dot ${i === 0 ? 'active' : ''}" onclick="event.stopPropagation(); galleryGo(${i})"></div>`).join('')}
          </div>
          <div class="detail-gallery-expand-hint" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>
            <span>Tap to expand</span>
          </div>
        ` : ''}
      ` : renderListingPhoto(null, 'detail-gallery-empty', 'Home photo')}
    </div>
    <button class="detail-back-btn" onclick="goBack()">
      <i data-feather="arrow-left" width="18"></i>
    </button>
    <button class="detail-share-btn" onclick="shareListing('${escapeHtml(listing.id)}')" aria-label="Share this listing" title="Share this listing">
      <i data-feather="share-2" width="18"></i>
    </button>

    <!-- Sticky jump links for the page's major zones (initDetailSubnav). -->
    <nav class="detail-subnav" aria-label="Jump to section">
      ${DETAIL_ZONES.map((z) => `<button type="button" class="detail-subnav-link" data-target="${z.target}">${z.label}</button>`).join('')}
    </nav>

    <div class="detail-content">
      <div class="detail-price">${formatPrice(listing.price)}</div>
      <div class="detail-address">${escapeHtml(formatListingAddress(listing))}${listing.distance_miles != null ? ` &middot; ${listing.distance_miles} mi away` : ''}</div>

      <div class="detail-stats">
        <div class="detail-stat">
          <div class="detail-stat-value">${listing.beds || '—'}</div>
          <div class="detail-stat-label">Beds</div>
        </div>
        <div class="detail-stat">
          <div class="detail-stat-value">${listing.baths || '—'}</div>
          <div class="detail-stat-label">Baths</div>
        </div>
        <div class="detail-stat">
          <div class="detail-stat-value">${listing.sqft ? listing.sqft.toLocaleString() : '—'}</div>
          <div class="detail-stat-label">Sqft</div>
        </div>
        ${listing.lot_sqft ? `
        <div class="detail-stat">
          <div class="detail-stat-value">${listing.lot_sqft.toLocaleString()}</div>
          <div class="detail-stat-label">Lot Sqft</div>
        </div>` : ''}
        ${listing.year_built ? `
        <div class="detail-stat">
          <div class="detail-stat-value">${listing.year_built}</div>
          <div class="detail-stat-label">Built</div>
        </div>` : ''}
      </div>
      ${listing.published_at ? (() => {
        const daysOnMarket = Math.floor((Date.now() - new Date(listing.published_at).getTime()) / (1000 * 60 * 60 * 24));
        return `<div style="text-align:center;font-size:13px;color:var(--text-secondary);margin-top:8px">${daysOnMarket} day${daysOnMarket !== 1 ? 's' : ''} on market</div>`;
      })() : ''}

      <!-- Verdict strip — filled by investorDetail.renderScoreStrip from the
           same analysis payload as Deal Analysis (no separate calculation). -->
      <div class="detail-score-strip score-strip-loading" id="detail-score-strip" aria-live="polite">
        <div class="spinner spinner-sm"></div>
        <span>Scoring this deal...</span>
      </div>

      <!-- The page's single demo-data notice (replaces the per-section
           badges in Details, Deal Analysis and Neighborhood IQ). -->
      <div class="demand-disclaimer detail-demo-banner">
        <span class="disclaimer-icon">i</span>
        <span class="disclaimer-text">Demo data \u2014 property details, deal figures and neighborhood data are estimates for preview. Verify independently.</span>
      </div>


      <!-- ═══════════════════════════════════════════════════════
           Narrative + specs — "why should I care?" + "does it fit?"
           ═══════════════════════════════════════════════════════ -->

      ${listing.description ? `
        <div class="detail-section">
          <h3><i data-feather="file-text" width="14" style="display:inline;vertical-align:-2px;margin-right:4px"></i> About</h3>
          <div class="detail-description">${escapeHtml(listing.description)}</div>
        </div>
      ` : ''}

      <div class="detail-section">
        <h3><i data-feather="list" width="14" style="display:inline;vertical-align:-2px;margin-right:4px"></i> Details</h3>
        ${_renderDetailsSection(listing)}
      </div>

      <!-- Investor-first: "Your Deal" block renders for everyone. Cap rate,
           CoC, DSCR, cash flow, GRM — the core deal-analysis numbers a buyer
           or investor needs before making a move. -->
      ${renderInvestorDealBlock(listing)}


      <!-- ═══════════════════════════════════════════════════════
           TRUST BLOCK — Photo-Truth™ → True Cost™ → HomeCheck™
           Placed right after the narrative + specs so buyers land
           on verification, cost transparency, and condition data
           exactly when they're asking "is it real? can I afford it?
           is it safe?" — and before neighborhood/social/agent
           sections where trust would get buried.
           ═══════════════════════════════════════════════════════ -->

      <div class="detail-section detail-section-trust" id="photo-truth-section">
        <h3><i data-feather="camera" width="14" style="display:inline;vertical-align:-2px;margin-right:4px"></i> Photo-Truth&trade; — Verified Listing Photos</h3>
        <div id="photo-truth-content">
          ${listing.reality_score != null ? (() => {
            const s = listing.reality_score;
            const color = s >= 80 ? 'var(--success)' : s >= 60 ? 'var(--warning)' : s >= 40 ? '#f97316' : 'var(--danger)';
            const label = s >= 80 ? 'Verified Authentic' : s >= 60 ? 'Minor Enhancements' : s >= 40 ? 'Significant Editing' : 'Heavy Manipulation';
            const badgeIcon = s >= 80 ? '\u2713' : s >= 60 ? '\u2022' : '\u26a0';
            return `
              <div class="pt-badge" style="display:inline-flex;align-items:center;gap:6px;padding:6px 12px;border-radius:999px;background:${color};color:#fff;font-weight:700;font-size:13px;margin-bottom:12px">
                <span>${badgeIcon}</span>
                <span>${label}</span>
              </div>
              <div style="display:flex;align-items:center;gap:16px;margin-bottom:12px">
                <div style="font-size:36px;font-weight:800;color:${color};line-height:1">${s}</div>
                <div>
                  <div style="font-weight:600;color:${color};font-size:15px">${label}</div>
                  <div style="font-size:12px;color:var(--text-muted);margin-top:2px">Reality Score</div>
                </div>
              </div>
              <div id="photo-truth-issues"></div>
              <div style="font-size:11px;color:var(--text-placeholder);margin-top:8px">Powered by Photo-Truth\u2122</div>
            `;
          })() : `
            <div style="display:flex;align-items:center;gap:8px;padding:8px 0;color:var(--text-secondary)">
              <div class="spinner spinner-sm"></div>
              <span style="font-size:14px">Verifying photos...</span>
            </div>
          `}
        </div>
      </div>

      <!-- Redundancy #3: True Cost section removed in QA pass 2. Your Deal's
           "Income + expense breakdown" already covers carrying costs (PITI +
           tax + insurance + property mgmt + maintenance + HOA), plus NOI,
           tax shield, after-tax cash flow — everything an investor needs.
           True Cost's "hidden costs" framing was buyer-residual; investors
           already see the same numbers as line items in the breakdown. The
           True Cost loader / local carrying-cost calc were deleted with it;
           the backend /true-cost endpoint remains. -->

      <div class="detail-section detail-section-trust" id="history-section">
        <h3><i data-feather="clock" width="14" style="display:inline;vertical-align:-2px;margin-right:4px"></i> Property History</h3>
        <div id="history-content">
          <div style="display:flex;align-items:center;gap:8px;padding:12px 0;color:var(--text-secondary)">
            <div class="spinner spinner-sm"></div>
            <span style="font-size:14px">Loading history...</span>
          </div>
        </div>
      </div>

      <div class="detail-section detail-section-trust" id="homecheck-section">
        <h3><i data-feather="shield" width="14" style="display:inline;vertical-align:-2px;margin-right:4px"></i> HomeCheck&trade; — Property Condition Report</h3>
        <div id="homecheck-content">
          <div style="display:flex;align-items:center;gap:8px;padding:12px 0;color:var(--text-secondary)">
            <div class="spinner spinner-sm"></div>
            <span style="font-size:14px">Checking property health...</span>
          </div>
        </div>
      </div>

      ${providerCtaSlot('condition_report')}

      <div class="detail-section" id="neighborhood-section">
        <h3><i data-feather="map-pin" width="14" style="display:inline;vertical-align:-2px;margin-right:4px"></i> Neighborhood IQ\u2122</h3>
        <div id="neighborhood-content">
          <div style="display:flex;align-items:center;gap:8px;padding:12px 0;color:var(--text-secondary)">
            <div class="spinner spinner-sm"></div>
            <span style="font-size:14px">Loading neighborhood data...</span>
          </div>
        </div>
      </div>


      <!-- Offer Insight was removed (investor-pov-fix-1): Offer Strategy
           (rendered inside renderInvestorDealBlock above) already gives the
           investor a specific cap-rate-anchored offer number with win-prob
           and a negotiation playbook. The legacy Offer Insight section
           rendered duplicate ground and the data pipeline was broken \u2014 list
           price $0 / DOM "undefined" \u2014 undermining the working Offer Strategy
           sitting right above it. Killing the section, not the data. -->

    </div>

    <div class="listing-attribution">
      <div class="attribution-text">
        Listing data provided via public records and third-party data providers.
        Listing information is deemed reliable but not guaranteed.
        ${listing.listing_agent_name ? `Listed by ${escapeHtml(listing.listing_agent_name)}${listing.listing_agent_brokerage ? ' at ' + escapeHtml(listing.listing_agent_brokerage) : ''}.` : ''}
      </div>
      <div class="attribution-logos">
        <span class="equal-housing-text">Equal Housing Opportunity</span>
      </div>
    </div>
  `;

  // Load neighborhood, true cost, HomeCheck, PhotoTruth, and the investor
  // deal / financing / rent-comp / market-position / offer-strategy data.
  // All fire for every buyer-role user — investor-first convergence means
  // there is no buyer-only or investor-only loader path. Dropped surfaces
  // (no longer load anything): Affordability, Offer Insight (data lives
  // in Your Deal + Offer Strategy), and now True Cost (data lives in
  // Your Deal's income+expense breakdown after redundancy #3).
  setTimeout(() => {
    loadHomeCheck(listing as unknown as Parameters<typeof loadHomeCheck>[0]);
    loadPhotoTruthEnhanced(listing);
    loadNeighborhoodIQ(listing);
    loadPropertyHistory(listing.id as string);
    loadInvestorDetail(listing.id as string);
    // loadActiveOfferForListing removed in the investor-first cleanup.
    mountProviderCtas(listing.id as string);
    initDetailSubnav(div);
  }, 50);

  return div;
}

let _galleryMedia: AnyRecord[] = [];
function galleryNav(dir: number): void {
  const img = document.getElementById('detail-img') as HTMLImageElement | null;
  if (!img) return;
  const listing = state.selectedListing;
  _galleryMedia = getAllMedia(listing as AnyRecord) as AnyRecord[];
  let idx = parseInt(img.dataset.index || '0', 10) + dir;
  if (idx < 0) idx = _galleryMedia.length - 1;
  if (idx >= _galleryMedia.length) idx = 0;
  img.src = _galleryMedia[idx].url as string;
  img.dataset.index = String(idx);
  document.querySelectorAll('.image-dot').forEach((d, i) => d.classList.toggle('active', i === idx));
}

function galleryGo(idx: number): void {
  const img = document.getElementById('detail-img') as HTMLImageElement | null;
  if (!img) return;
  const listing = state.selectedListing;
  const media = getAllMedia(listing as AnyRecord) as AnyRecord[];
  if (media[idx]) {
    img.src = media[idx].url as string;
    img.dataset.index = String(idx);
    document.querySelectorAll('.image-dot').forEach((d, i) => d.classList.toggle('active', i === idx));
  }
}

// ─── Listing Claim Flow ────────────────────────────────


// ─── Neighborhood Intel Loader ─────────────────────────
async function loadNeighborhood(listingId: string): Promise<void> {
  const container = document.getElementById('neighborhood-content');
  if (!container) return;

  try {
    const data = await api<AnyRecord>('GET', `/listings/${listingId}/neighborhood`);
    if (!data || data.status === 'pending' || !data.summary) {
      const listing = (listingStash().detailListing as AnyRecord) || {};
      const lat = (listing.latitude || listing.lat) as number | undefined;
      const lng = (listing.longitude || listing.lng) as number | undefined;
      const mapFallback = lat && lng ? `
        <div class="property-map" style="margin-bottom:14px">
          <iframe src="https://www.openstreetmap.org/export/embed.html?bbox=${lng-0.008},${lat-0.006},${lng+0.008},${lat+0.006}&layer=mapnik&marker=${lat},${lng}"
            width="100%" height="120" style="border:none;border-radius:10px" loading="lazy" title="Property location"></iframe>
          <a href="https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}" target="_blank" rel="noopener"
            style="display:inline-flex;align-items:center;gap:6px;margin-top:8px;color:var(--primary);font-size:13px;text-decoration:none">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
            Get Directions
          </a>
        </div>` : '';
      container.innerHTML = `
        ${mapFallback}
        <div class="card-flat" style="text-align:center;padding:20px">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--text-placeholder)" stroke-width="1.5" style="margin-bottom:8px"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
          <p style="font-size:14px;color:var(--text-secondary);margin:0">Neighborhood details not yet available for this listing.</p>
          <p style="font-size:12px;color:var(--text-placeholder);margin:4px 0 0">School ratings, walkability scores, and nearby amenities will appear here when data is available.</p>
        </div>`;
      return;
    }

    const overallScore = parseFloat(String(data.overall_score)) || 0;
    const scoreColor = overallScore >= 8 ? 'var(--success)' : overallScore >= 6 ? 'var(--warning)' : 'var(--danger)';
    const scores = (data.scores as AnyRecord) || {};

    interface ColorTriple { bg: string; color: string; border: string; }
    const vibeColors: Record<string, ColorTriple> = {
      'urban': { bg: 'var(--primary-bg)', color: 'var(--primary)', border: 'var(--primary-border)' },
      'suburban': { bg: 'var(--success-bg)', color: 'var(--success)', border: 'var(--success-border)' },
      'rural': { bg: 'var(--warning-bg)', color: 'var(--warning)', border: 'var(--warning-border)' },
      'up-and-coming': { bg: 'var(--primary-bg)', color: 'var(--primary-light)', border: 'var(--primary-border)' },
      'established': { bg: 'var(--success-bg)', color: 'var(--success)', border: 'var(--success-border)' },
    };
    const vc = vibeColors[data.vibe as string] || vibeColors['suburban'];

    const appreciationColors: Record<string, ColorTriple> = {
      'high': { bg: 'var(--success-bg)', color: 'var(--success)', border: 'var(--success-border)' },
      'moderate': { bg: 'var(--warning-bg)', color: 'var(--warning)', border: 'var(--warning-border)' },
      'low': { bg: 'var(--danger-bg)', color: 'var(--danger)', border: 'var(--danger-border)' },
    };
    const ac = appreciationColors[data.appreciation_potential as string] || appreciationColors['moderate'];

    const scoreLabels = {
      safety: 'Safety',
      schools: 'Schools',
      walkability: 'Walkability',
      transit: 'Transit',
      dining_entertainment: 'Dining',
      family_friendly: 'Family',
    };

    container.innerHTML = `
      <div style="display:flex;align-items:center;gap:16px;margin-bottom:16px">
        <div style="text-align:center;min-width:64px">
          <div style="font-size:36px;font-weight:800;color:${scoreColor};line-height:1">${overallScore.toFixed(1)}</div>
          <div style="font-size:11px;color:var(--text-secondary);margin-top:2px">out of 10</div>
        </div>
        <div style="flex:1">
          <p style="font-size:14px;color:var(--text-muted);line-height:1.5">${escapeHtml(data.summary)}</p>
        </div>
      </div>

      <div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:16px">
        ${data.vibe ? `<span class="badge" style="background:${vc.bg};color:${vc.color};border:1px solid ${vc.border}">${escapeHtml(data.vibe)}</span>` : ''}
        ${data.appreciation_potential ? `<span class="badge" style="background:${ac.bg};color:${ac.color};border:1px solid ${ac.border}">${escapeHtml(data.appreciation_potential)} appreciation${data.estimated_annual_appreciation ? ' ~' + escapeHtml(data.estimated_annual_appreciation) : ''}</span>` : ''}
      </div>

      <div style="margin-bottom:16px">
        ${Object.entries(scoreLabels).map(([key, label]) => {
          const val = parseFloat(scores[key]) || 0;
          const barColor = val >= 8 ? 'var(--success)' : val >= 6 ? 'var(--warning)' : 'var(--danger)';
          return `
            <div style="display:flex;align-items:center;gap:10px;margin-bottom:8px">
              <div style="width:70px;font-size:12px;color:var(--text-secondary);text-align:right">${label}</div>
              <div style="flex:1;height:8px;background:var(--border);border-radius:4px;overflow:hidden">
                <div style="width:${val * 10}%;height:100%;background:${barColor};border-radius:4px;transition:width 0.5s ease"></div>
              </div>
              <div style="width:24px;font-size:12px;font-weight:600;color:var(--text-muted)">${val.toFixed(0)}</div>
            </div>
          `;
        }).join('')}
      </div>

      ${((data.highlights as string[]) || []).length > 0 ? `
        <div style="margin-bottom:12px">
          ${(data.highlights as string[]).map((h: string) => `
            <div style="display:flex;align-items:flex-start;gap:8px;margin-bottom:6px">
              <i data-feather="check-circle" width="16" style="color:var(--success);flex-shrink:0;margin-top:2px"></i>
              <span style="font-size:14px;color:var(--text-muted)">${escapeHtml(h)}</span>
            </div>
          `).join('')}
        </div>
      ` : ''}

      ${((data.considerations as string[]) || []).length > 0 ? `
        <div style="margin-bottom:12px">
          ${(data.considerations as string[]).map((c: string) => `
            <div style="display:flex;align-items:flex-start;gap:8px;margin-bottom:6px">
              <i data-feather="alert-triangle" width="16" style="color:var(--warning);flex-shrink:0;margin-top:2px"></i>
              <span style="font-size:14px;color:var(--text-muted)">${escapeHtml(c)}</span>
            </div>
          `).join('')}
        </div>
      ` : ''}

      ${data.commute_notes ? `
        <div class="card-flat" style="margin-top:8px">
          <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px">
            <i data-feather="navigation" width="14" style="color:var(--primary)"></i>
            <span style="font-size:13px;font-weight:600">Commute</span>
          </div>
          <div style="font-size:14px;color:var(--text-muted)">${escapeHtml(data.commute_notes)}</div>
        </div>
      ` : ''}

      <p style="font-size:11px;color:var(--text-placeholder);margin-top:12px;font-style:italic">Based on available data — may not reflect current conditions</p>
    `;

    if (window.feather) window.feather.replace();
  } catch (err: any) {
    container.innerHTML = '<p class="text-secondary" style="font-size:14px">Neighborhood data not available.</p>';
  }
}

// ─── PhotoTruth Loader ───────────────────────────────
// Honest empty state for listings with no photos: Photo-Truth has
// nothing to verify, so say so instead of spinning "in progress"
// forever (the backend returns status "no_photos" for these).
function renderPhotoTruthEmpty(container: HTMLElement): void {
  container.innerHTML = '<p class="text-secondary" style="font-size:14px">No photos to verify — add listing photos to run Photo-Truth&trade;.</p>';
}

function _formatIssueLabel(item: unknown): string {
  // Handle both string issues (legacy) and object issues (v2 API)
  if (typeof item === 'string') return item;
  const obj = item as AnyRecord;
  const raw = (obj.category as string) || (obj.type as string) || 'unknown';
  return raw.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
}

async function loadPhotoTruth(listingId: string): Promise<void> {
  const container = document.getElementById('photo-truth-content');
  if (!container) return;

  try {
    const data = await api<AnyRecord>('GET', `/listings/${listingId}/photo-analysis`);
    if (data.status === 'no_photos') {
      renderPhotoTruthEmpty(container);
      return;
    }
    if (data.status === 'pending' || data.status === 'analyzing') {
      // No background analysis queue exists — "pending" means no v2
      // analysis has ever run for this listing, not that one is underway.
      container.innerHTML = '<p class="text-secondary" style="font-size:14px">Photo analysis hasn\'t run for this listing yet.</p>';
      return;
    }

    if (data.status === 'complete' && data.overall_score != null) {
      const s = data.overall_score;
      const color = s >= 80 ? 'var(--success)' : s >= 60 ? 'var(--warning)' : s >= 40 ? '#f97316' : 'var(--danger)';
      const label = s >= 80 ? 'Verified Authentic' : s >= 60 ? 'Minor Enhancements' : s >= 40 ? 'Significant Editing' : 'Heavy Manipulation';

      const issues = ((data.results as AnyRecord[]) || [])
        .flatMap((r: AnyRecord) => (r.issues as unknown[]) || [])
        .filter(Boolean);
      // Deduplicate by category/type (objects don't dedup with Set)
      const seen = new Set<string>();
      const uniqueIssues = issues.filter((i: unknown) => {
        const obj = i as AnyRecord;
        const key = typeof i === 'string' ? i : ((obj.category as string) || (obj.type as string) || JSON.stringify(i));
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      container.innerHTML = `
        <div style="display:flex;align-items:center;gap:16px;margin-bottom:12px">
          <div style="font-size:36px;font-weight:800;color:${color};line-height:1">${s}</div>
          <div>
            <div style="font-weight:600;color:${color};font-size:15px">${label}</div>
            <div style="font-size:12px;color:var(--text-muted);margin-top:2px">Reality Score &bull; ${data.analyzed} photo${data.analyzed !== 1 ? 's' : ''} analyzed</div>
          </div>
        </div>
        ${uniqueIssues.length > 0 ? `<div style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:8px">${uniqueIssues.map((i: unknown) => {
          const displayLabel = _formatIssueLabel(i);
          const obj = i as AnyRecord;
          const tooltip = typeof i === 'object' && obj?.description ? ` title="${escapeHtml(obj.description as string)}"` : '';
          return `<span class="chip" style="font-size:11px;background:${s >= 80 ? 'var(--success-bg);color:var(--success)' : 'var(--warning-bg);color:var(--warning)'}"${tooltip}>${escapeHtml(displayLabel)}</span>`;
        }).join('')}</div>` : ''}
        <div style="font-size:11px;color:var(--text-placeholder);margin-top:8px">Powered by Photo-Truth\u2122</div>
      `;
    }
  } catch (err: any) {
    container.innerHTML = '<p class="text-secondary" style="font-size:14px">Photo-Truth&trade; analysis not available.</p>';
  }
}

// ═══════════════════════════════════════════════════════
// Tier 2 — Property history timeline
// ═══════════════════════════════════════════════════════
interface HistoryEvent {
  detected_at: string | null;
  change_type: string;
  previous_price?: number | null;
  new_price?: number | null;
  price_diff?: number | null;
  direction?: 'up' | 'down';
  previous_status?: string | null;
  new_status?: string | null;
}

async function loadPropertyHistory(listingId: string): Promise<void> {
  const container = document.getElementById('history-content');
  if (!container) return;
  try {
    const data = await api<AnyRecord>('GET', `/listings/${listingId}/history`);
    const events = (data.events as HistoryEvent[]) || [];
    const dom = (data.days_on_market as number | null) ?? null;

    if (events.length === 0) {
      container.innerHTML = `
        <div class="history-empty">
          <p class="history-empty-text">No recorded changes since listed.</p>
          ${dom != null ? `<p class="history-empty-dom">${dom} ${dom === 1 ? 'day' : 'days'} on market</p>` : ''}
        </div>
      `;
      return;
    }

    const domBadge = dom != null
      ? `<div class="history-dom-badge">${dom} ${dom === 1 ? 'day' : 'days'} on market</div>`
      : '';

    const timeline = events
      .map((ev) => {
        const date = ev.detected_at
          ? new Date(ev.detected_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
          : '—';

        let icon = 'activity';
        let iconColor = 'var(--primary)';
        let title = ev.change_type;
        let body = '';

        if (ev.change_type === 'listed') {
          icon = 'home';
          iconColor = 'var(--primary)';
          title = 'Listed';
          body = ev.new_price != null
            ? `<span class="history-price">${formatPrice(ev.new_price)}</span>`
            : '';
        } else if (ev.change_type === 'price_change' || ev.direction) {
          const isDrop = ev.direction === 'down';
          icon = isDrop ? 'trending-down' : 'trending-up';
          iconColor = isDrop ? 'var(--success)' : 'var(--danger)';
          title = isDrop ? 'Price reduced' : 'Price increased';
          const diffLabel = ev.price_diff != null
            ? `${isDrop ? '\u2212' : '+'}${formatPrice(Math.abs(ev.price_diff))}`
            : '';
          body = `
            <span class="history-price-row">
              ${ev.previous_price != null ? `<span class="history-price-old">${formatPrice(ev.previous_price)}</span>` : ''}
              <i data-feather="arrow-right" width="12" style="color:var(--text-muted)"></i>
              ${ev.new_price != null ? `<span class="history-price" style="color:${iconColor}">${formatPrice(ev.new_price)}</span>` : ''}
              ${diffLabel ? `<span class="history-price-diff" style="color:${iconColor}">${diffLabel}</span>` : ''}
            </span>
          `;
        } else if (ev.change_type === 'status_change') {
          icon = 'info';
          iconColor = 'var(--primary)';
          title = 'Status updated';
          body = `<span class="history-status">${escapeHtml(ev.previous_status || '')} \u2192 ${escapeHtml(ev.new_status || '')}</span>`;
        } else {
          // Generic fallback — displays the raw change_type cleaned up
          title = ev.change_type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
        }

        return `
          <div class="history-event">
            <div class="history-event-icon" style="color:${iconColor}">
              <i data-feather="${icon}" width="16"></i>
            </div>
            <div class="history-event-body">
              <div class="history-event-title">${escapeHtml(title)}</div>
              ${body}
              <div class="history-event-date">${escapeHtml(date)}</div>
            </div>
          </div>
        `;
      })
      .join('');

    container.innerHTML = `
      ${domBadge}
      <div class="history-timeline">${timeline}</div>
    `;
    if (window.feather) window.feather.replace();
  } catch (err: any) {
    container.innerHTML = `<p class="text-secondary" style="font-size:14px;padding:8px 0">History unavailable: ${escapeHtml(err.message)}</p>`;
  }
}

// ─── Enhanced PhotoTruth Loader (5-signal forensics) ────────────
async function loadPhotoTruthEnhanced(listing: AnyRecord): Promise<void> {
  const container = document.getElementById('photo-truth-content');
  if (!container) return;
  const address = (listing.address_line1 as string) || (listing.address as string) || '';

  if (!isHomecheckDemo() || !address) {
    if (listing.reality_score == null) loadPhotoTruth(listing.id as string);
    return;
  }

  try {
    const data = await api<AnyRecord>('GET', `/trust/phototruth/${listing.id}?address=${encodeURIComponent(address)}`);
    // Definitive "no photos" — render the empty state directly instead
    // of falling back to the legacy loader (which would also resolve
    // to no_photos, but only after a wasted second request).
    if (data.status === 'no_photos') {
      renderPhotoTruthEmpty(container);
      return;
    }
    if (data.available === false) {
      if (listing.reality_score == null) loadPhotoTruth(listing.id as string);
      return;
    }

    // Map trust levels to display info
    interface TrustDisplay { label: string; color: string; icon: string; bg: string; }
    const trustDisplayMap: Record<string, TrustDisplay> = {
      verified:             { label: 'Verified Authentic',     color: 'var(--success)', icon: 'check',          bg: 'var(--success-bg)' },
      authentic:            { label: 'Authentic Capture',      color: 'var(--success)', icon: 'check',          bg: 'var(--success-bg)' },
      standard:             { label: 'Standard RE Photography',color: 'var(--success)', icon: 'camera',         bg: 'var(--success-bg)' },
      lightly_enhanced:     { label: 'Lightly Enhanced',       color: 'var(--success)', icon: 'sun',            bg: 'var(--success-bg)' },
      professionally_edited:{ label: 'Professionally Edited',  color: 'var(--warning)', icon: 'edit-2',         bg: 'var(--warning-bg)' },
      heavily_enhanced:     { label: 'Heavily Enhanced',       color: '#f97316',        icon: 'alert-triangle', bg: 'rgba(249,115,22,0.12)' },
      virtually_staged:     { label: 'Virtually Staged',       color: 'var(--danger)',  icon: 'alert-triangle', bg: 'var(--danger-bg)' },
      ai_suspected:         { label: 'AI Content Suspected',   color: 'var(--danger)',  icon: 'alert-circle',   bg: 'var(--danger-bg)' },
      ai_generated:         { label: 'AI Generated',           color: 'var(--danger)',  icon: 'x-circle',       bg: 'var(--danger-bg)' },
      unknown:              { label: 'Analysis Inconclusive',  color: 'var(--text-muted)', icon: 'help-circle', bg: 'var(--surface-alt)' },
    };

    const trust = (data.overall_trust as string) || 'unknown';
    const display = trustDisplayMap[trust] || trustDisplayMap.unknown;
    const score = (data.overall_score as number) != null ? (data.overall_score as number) : 50;
    const scoreColor = score >= 80 ? 'var(--success)' : score >= 60 ? 'var(--warning)' : score >= 40 ? '#f97316' : 'var(--danger)';
    const isClean = ['verified', 'authentic', 'standard', 'lightly_enhanced'].includes(trust);

    // Collect enhancement types as readable badges
    const enhancementTypes = ((data.enhancement_types_found as unknown[]) || []).map((cat: unknown) => {
      const obj = cat as AnyRecord;
      const label = typeof cat === 'string' ? cat : ((obj.category as string) || (obj.type as string) || '');
      return label.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
    }).filter(Boolean);

    // Collect per-photo issues for detail view
    interface PhotoIssue { photoNum: number; description: string; severity: string; category: string; }
    const photoIssues: PhotoIssue[] = ((data.photos as AnyRecord[]) || []).flatMap((photo: AnyRecord, idx: number) => {
      return ((photo.enhancements_detected as unknown[]) || []).map((enh: unknown) => {
        const obj = enh as AnyRecord;
        return {
          photoNum: ((photo.photo_index as number) != null ? (photo.photo_index as number) : idx) + 1,
          description: typeof enh === 'string' ? enh : ((obj.description as string) || (obj.type as string) || 'Enhancement detected'),
          severity: typeof enh === 'object' ? ((obj.severity as string) || 'low') : 'low',
          category: typeof enh === 'object' ? ((obj.category as string) || (obj.type as string) || '') : (enh as string),
        };
      });
    });

    container.innerHTML = `
      <div style="display:flex;align-items:center;gap:14px;margin-bottom:14px">
        <div style="display:flex;flex-direction:column;align-items:center;line-height:1">
          <div style="font-size:36px;font-weight:800;color:${scoreColor}">${score}</div>
          <div style="font-size:10px;font-weight:700;color:var(--text-muted);letter-spacing:0.5px;margin-top:2px">REALITY SCORE</div>
        </div>
        <div style="flex:1">
          <div style="display:flex;align-items:center;gap:6px">
            <div style="width:28px;height:28px;border-radius:50%;background:${display.bg};display:flex;align-items:center;justify-content:center">
              <i data-feather="${display.icon}" width="14" style="color:${display.color}"></i>
            </div>
            <div style="font-weight:700;color:${display.color};font-size:15px">${display.label}</div>
          </div>
          <div style="font-size:12px;color:var(--text-muted);margin-top:3px">
            ${data.photos_analyzed || 0} photos analyzed${data.sightengine_checked ? ` &middot; ${data.sightengine_checked} AI-scanned` : ''}
          </div>
        </div>
      </div>
      ${enhancementTypes.length > 0 ? `
        <div style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:10px">
          ${enhancementTypes.map((t: string) => `<span class="chip" style="font-size:11px;background:${isClean ? 'var(--success-bg);color:var(--success)' : 'var(--warning-bg);color:var(--warning)'}">${escapeHtml(t)}</span>`).join('')}
        </div>
      ` : ''}
      ${isClean ? `
        <div class="pt-checks">
          <div class="pt-check-row"><i data-feather="check" width="14" style="color:var(--success)"></i><span>No AI-generated images detected</span></div>
          <div class="pt-check-row"><i data-feather="check" width="14" style="color:var(--success)"></i><span>EXIF metadata analyzed</span></div>
          <div class="pt-check-row"><i data-feather="check" width="14" style="color:var(--success)"></i><span>Error Level Analysis passed</span></div>
          <div class="pt-check-row"><i data-feather="check" width="14" style="color:var(--success)"></i><span>Pixel statistics normal</span></div>
        </div>
      ` : `
        <div class="pt-issues">
          ${photoIssues.slice(0, 5).map((iss: PhotoIssue) => `
            <div class="pt-issue-row">
              <div class="pt-issue-header">
                <i data-feather="${iss.severity === 'high' ? 'alert-circle' : 'alert-triangle'}" width="13" style="color:${iss.severity === 'high' ? 'var(--danger)' : 'var(--warning)'}"></i>
                <span>Photo ${iss.photoNum}: ${escapeHtml(iss.description)}</span>
              </div>
            </div>
          `).join('')}
          ${photoIssues.length > 5 ? `<div style="font-size:12px;color:var(--text-muted);margin-top:4px">+${photoIssues.length - 5} more findings</div>` : ''}
        </div>
        <div style="display:flex;align-items:center;gap:6px;margin-top:12px;font-size:12px;color:var(--primary)">
          <i data-feather="info" width="13"></i>
          <span>Ask the seller for unedited photos</span>
        </div>
      `}
      <div style="font-size:11px;color:var(--text-placeholder);margin-top:10px">
        Powered by Photo-Truth\u2122 &middot; 5-signal forensic analysis
      </div>
    `;
    if (window.feather) window.feather.replace();
  } catch (_e: any) {
    if (listing.reality_score == null) loadPhotoTruth(listing.id as string);
  }
}

// ─── Enhanced Neighborhood IQ Loader ──────────────────
async function loadNeighborhoodIQ(listing: AnyRecord): Promise<void> {
  const container = document.getElementById('neighborhood-content');
  if (!container) return;
  const address = (listing.address_line1 as string) || (listing.address as string) || '';

  if (!isHomecheckDemo() || !address) {
    loadNeighborhood(listing.id as string);
    return;
  }

  try {
    const data = await api<AnyRecord>('GET', `/trust/neighborhood-iq/${listing.id}?address=${encodeURIComponent(address)}`);
    if (data.available === false) {
      loadNeighborhood(listing.id as string);
      return;
    }

    function scoreColor(s: number): string {
      return s >= LEAD_TIER_HOT ? 'var(--success)' : s >= LEAD_TIER_WARM ? 'var(--warning)' : 'var(--danger)';
    }

    const lat = (listing.latitude || listing.lat) as number | undefined;
    const lng = (listing.longitude || listing.lng) as number | undefined;
    const mapHtml = lat && lng ? `
      <div class="property-map" style="margin-bottom:14px">
        <iframe src="https://www.openstreetmap.org/export/embed.html?bbox=${lng-0.008},${lat-0.006},${lng+0.008},${lat+0.006}&layer=mapnik&marker=${lat},${lng}"
          width="100%" height="120" style="border:none;border-radius:10px" loading="lazy" title="Property location"></iframe>
        <a href="https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}" target="_blank" rel="noopener"
          style="display:inline-flex;align-items:center;gap:6px;margin-top:8px;color:var(--primary);font-size:13px;text-decoration:none">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
          Get Directions
        </a>
      </div>
    ` : '';

    container.innerHTML = `
      ${mapHtml}
      <details class="inv-deal-breakdown inv-collapse niq-collapse">
      <summary>
      <div class="niq-scores-row">
        <div class="niq-score-card">
          <div class="niq-score-val" style="color:${scoreColor(data.walk_score)}">${data.walk_score}</div>
          <div class="niq-score-icon"><i data-feather="user" width="14"></i></div>
          <div class="niq-score-label">Walk Score</div>
        </div>
        <div class="niq-score-card">
          <div class="niq-score-val" style="color:${scoreColor(data.transit_score)}">${data.transit_score}</div>
          <div class="niq-score-icon"><i data-feather="navigation" width="14"></i></div>
          <div class="niq-score-label">Transit Score</div>
        </div>
        <div class="niq-score-card">
          <div class="niq-score-val" style="color:${scoreColor(data.bike_score)}">${data.bike_score}</div>
          <div class="niq-score-icon"><i data-feather="wind" width="14"></i></div>
          <div class="niq-score-label">Bike Score</div>
        </div>
      </div>
      <span class="niq-collapse-hint">Amenities &amp; schools <i data-feather="chevron-down" width="14"></i></span>
      </summary>

      <div style="font-size:11px;font-weight:600;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.5px;margin:14px 0 8px">Nearby Amenities</div>
      <div class="niq-amenities" role="list">
        ${(data.amenities as AnyRecord[]).map((a: AnyRecord) => amenityRowHtml(a as AmenityLike, escapeHtml)).join('')}
      </div>

      <div style="font-size:11px;font-weight:600;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.5px;margin:14px 0 8px">Schools</div>
      <div class="niq-schools">
        ${(data.schools as AnyRecord[]).map((s: AnyRecord) => `
          <div class="niq-school-row">
            <div class="niq-school-icon"><i data-feather="book-open" width="14"></i></div>
            <div class="niq-school-info">
              <span class="niq-school-name">${escapeHtml(s.name)}</span>
              <span class="niq-school-type">${escapeHtml(s.type)} &middot; ${distanceTile(s.distance)}</span>
            </div>
            <div class="niq-school-rating" style="color:${s.rating >= 7 ? 'var(--success)' : s.rating >= 5 ? 'var(--warning)' : 'var(--danger)'}">${s.rating}/10</div>
          </div>
        `).join('')}
      </div>
      </details>
      <p style="font-size:11px;color:var(--text-placeholder);margin-top:12px;font-style:italic">Based on available data — may not reflect current conditions</p>
    `;
    if (window.feather) window.feather.replace();
  } catch (_e: any) {
    loadNeighborhood(listing.id as string);
  }
}


// ═══════════════════════════════════════════════════════
// Offer Insight + Offer Advice loaders (loadOfferInsight, loadOfferAI,
// _offerInsightFooter) were removed in investor-pov-fix #3. Offer
// Strategy — rendered inside renderInvestorDealBlock — already gives a
// cap-rate-anchored offer number with win probability + negotiation
// playbook for everyone. The legacy Offer Insight section duplicated
// the same ground and shipped with $0 / undefined data, undermining
// trust in the working Offer Strategy sitting right above it. The
// /listings/<id>/offer-insight and /listings/<id>/offer-advice backend
// endpoints still exist for now and can be re-mounted later if a use
// case appears.
// ═══════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════
// Legacy Investor Hub™ (loadInvestorHub, _renderInvestorHub,
// recalcInvestorHub, _renderIHResults, _ratingLabel, _ratingClass)
// was removed during the investor pivot. It was a seeded-random
// stub showing fake rent/cap rate estimates. The real deal
// analysis engine lives in services/investment_analyzer.py on the
// backend and renders via investorDetail.ts on the frontend —
// Deal Analysis + Financing Scenarios + Rent Comparables sections.
// ═══════════════════════════════════════════════════════

// Referenced only via inline onclick (window.loadDealAnalysis is not wired up today).
// Kept for parity with the legacy JS — re-enable by adding it to main.ts's window exposure.
async function _loadDealAnalysis(listingId: string): Promise<void> {
  const container = document.getElementById('deal-results');
  if (!container) return;
  const downEl = document.getElementById('deal-down') as HTMLInputElement | null;
  const rateEl = document.getElementById('deal-rate') as HTMLInputElement | null;
  const termEl = document.getElementById('deal-term') as HTMLInputElement | null;
  const downPct = downEl?.value || 20;
  const rate = rateEl?.value || 7.0;
  const term = termEl?.value || 30;

  if (!listingId) {
    container.innerHTML = '<p class="text-secondary">No listing selected.</p>';
    return;
  }

  container.innerHTML = '<p style="text-align:center;color:var(--text-secondary);padding:20px 0">Running the numbers...</p>';

  try {
    const res = await fetch(`${API}/listings/${listingId}/deal-analysis?down_payment_pct=${downPct}&interest_rate=${rate}&loan_term_years=${term}`, {
      headers: { 'Authorization': `Bearer ${state.token}` }
    });
    if (res.status === 401 || res.status === 403) throw new Error('Insufficient permissions');
    if (!res.ok) throw new Error('Analysis failed');
    const d = await res.json() as AnyRecord;
    const gradeColors: Record<string, string> = { A: '#22c55e', B: '#84cc16', C: '#eab308', D: '#f97316', F: '#ef4444' };
    const verdictColors: Record<string, string> = { 'strong buy': '#22c55e', 'buy': '#84cc16', 'hold': '#eab308', 'pass': '#ef4444' };
    const fitColors: Record<string, string> = { good: '#22c55e', fair: '#eab308', poor: '#ef4444' };
    const fitLabels: Record<string, string> = { good: 'Good', fair: 'Fair', poor: 'Poor' };

    const gradeColor = gradeColors[d.investment_grade as string] || '#6b7280';
    const verdictColor = verdictColors[d.verdict as string] || '#6b7280';

    const fmt = (v: number | null | undefined) => v != null ? '$' + Math.abs(v).toLocaleString() : '—';
    const fmtSigned = (v: number | null | undefined) => v != null ? (v < 0 ? '-' : '+') + '$' + Math.abs(v).toLocaleString() : '—';

    const strategyFit = (d.strategy_fit as AnyRecord) || {};
    const strategies = [
      { key: 'buy_and_hold', label: 'Buy & Hold' },
      { key: 'flip', label: 'Flip' },
      { key: 'brrrr', label: 'BRRRR' },
      { key: 'house_hack', label: 'House Hack' },
    ];

    container.innerHTML = `
      ${d.investment_grade || d.verdict ? `
        <div style="display:flex;gap:12px;align-items:center;margin-bottom:20px;flex-wrap:wrap">
          ${d.investment_grade ? `<div style="background:${gradeColor};color:#fff;font-size:28px;font-weight:800;width:56px;height:56px;border-radius:12px;display:flex;align-items:center;justify-content:center">${d.investment_grade}</div>` : ''}
          ${d.verdict ? `<span style="background:${verdictColor}22;color:${verdictColor};padding:6px 16px;border-radius:20px;font-weight:700;font-size:14px;text-transform:uppercase">${d.verdict}</span>` : ''}
        </div>
      ` : ''}

      <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:20px">
        <div style="background:var(--surface);border-radius:12px;padding:16px;text-align:center">
          <div style="font-size:24px;font-weight:800;color:var(--primary)">${d.cap_rate != null ? d.cap_rate + '%' : '—'}</div>
          <div style="font-size:12px;color:var(--text-secondary);margin-top:4px">Cap Rate</div>
        </div>
        <div style="background:var(--surface);border-radius:12px;padding:16px;text-align:center">
          <div style="font-size:24px;font-weight:800;color:${(d.cash_on_cash || 0) >= 0 ? '#22c55e' : '#ef4444'}">${d.cash_on_cash != null ? d.cash_on_cash + '%' : '—'}</div>
          <div style="font-size:12px;color:var(--text-secondary);margin-top:4px">Cash on Cash</div>
        </div>
      </div>

      <div style="background:var(--surface);border-radius:12px;padding:16px;margin-bottom:20px">
        <h4 style="margin:0 0 12px 0;font-size:14px;font-weight:700">Financial Breakdown</h4>
        <table style="width:100%;font-size:13px;border-collapse:collapse">
          <tr style="border-bottom:1px solid var(--border)"><td style="padding:6px 0;color:var(--text-secondary)">Est. Monthly Rent</td><td style="text-align:right;font-weight:600;color:#22c55e">${fmt(d.estimated_rent)}</td></tr>
          <tr style="border-bottom:1px solid var(--border)"><td style="padding:6px 0;color:var(--text-secondary)">Mortgage (P&I)</td><td style="text-align:right;font-weight:600">-${fmt(d.monthly_mortgage)}</td></tr>
          <tr style="border-bottom:1px solid var(--border)"><td style="padding:6px 0;color:var(--text-secondary)">Property Tax</td><td style="text-align:right;font-weight:600">-${fmt(d.property_tax)}</td></tr>
          <tr style="border-bottom:1px solid var(--border)"><td style="padding:6px 0;color:var(--text-secondary)">Insurance</td><td style="text-align:right;font-weight:600">-${fmt(d.insurance)}</td></tr>
          <tr style="border-bottom:1px solid var(--border)"><td style="padding:6px 0;color:var(--text-secondary)">Maintenance</td><td style="text-align:right;font-weight:600">-${fmt(d.maintenance)}</td></tr>
          ${d.hoa ? `<tr style="border-bottom:1px solid var(--border)"><td style="padding:6px 0;color:var(--text-secondary)">HOA</td><td style="text-align:right;font-weight:600">-${fmt(d.hoa)}</td></tr>` : ''}
          <tr><td style="padding:8px 0;font-weight:700;font-size:14px">Monthly Cash Flow</td><td style="text-align:right;font-weight:800;font-size:14px;color:${(d.monthly_cash_flow || 0) >= 0 ? '#22c55e' : '#ef4444'}">${fmtSigned(d.monthly_cash_flow)}</td></tr>
        </table>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:12px;padding-top:12px;border-top:1px solid var(--border);font-size:12px;color:var(--text-secondary)">
          <div>Down: ${fmt(d.down_payment)} (${d.down_payment_pct || 20}%)</div>
          <div>Loan: ${fmt(d.loan_amount)}</div>
          <div>NOI: ${fmt(d.noi)}/yr</div>
          <div>GRM: ${d.grm || '—'}</div>
        </div>
      </div>

      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:20px">
        ${strategies.map((s: { key: string; label: string }) => {
          const val = strategyFit[s.key] as string;
          const color = fitColors[val] || '#6b7280';
          return `<div style="background:var(--surface);border-radius:8px;padding:10px 12px;display:flex;justify-content:space-between;align-items:center">
            <span style="font-size:13px;font-weight:600">${s.label}</span>
            <span style="font-size:12px;font-weight:700;color:${color}">${fitLabels[val] || val || '—'}</span>
          </div>`;
        }).join('')}
      </div>

      ${d.summary ? `<p style="font-size:14px;line-height:1.6;color:var(--text-primary);margin-bottom:16px">${d.summary}</p>` : ''}

      ${d.strengths && d.strengths.length ? `
        <div style="margin-bottom:12px">
          <h4 style="margin:0 0 8px 0;font-size:13px;font-weight:700;color:#22c55e">Strengths</h4>
          <ul style="margin:0;padding-left:20px;font-size:13px;line-height:1.8;color:var(--text-secondary)">
            ${(d.strengths as string[]).map((s: string) => `<li>${s}</li>`).join('')}
          </ul>
        </div>
      ` : ''}

      ${d.risks && d.risks.length ? `
        <div style="margin-bottom:12px">
          <h4 style="margin:0 0 8px 0;font-size:13px;font-weight:700;color:#ef4444">Risks</h4>
          <ul style="margin:0;padding-left:20px;font-size:13px;line-height:1.8;color:var(--text-secondary)">
            ${(d.risks as string[]).map((r: string) => `<li>${r}</li>`).join('')}
          </ul>
        </div>
      ` : ''}
    `;
  } catch (err: any) {
    container.innerHTML = `<p class="text-secondary" style="font-size:14px">${err.message === 'Insufficient permissions' ? 'Sign in to access deal analysis.' : 'Deal analysis not available.'}</p>`;
  }
}
void _loadDealAnalysis;


// ═══════════════════════════════════════════════════════
// Buyer Intelligence (Agent Detail View)
// ═══════════════════════════════════════════════════════
listingStash()._biFilter = 'all';








// ── Details Section Builder ─────────────────────────────
// TODO: Remove demo data once backend returns extended fields
function _getDemoExtras(listing: AnyRecord): AnyRecord {
  const demos: AnyRecord[] = [
    { heating: 'Forced Air, Gas', cooling: 'Central Air', appliances: 'Dishwasher, Microwave, Refrigerator, Oven', flooring: 'Hardwood, Tile', interior_features: 'Walk-in Closet, Open Floor Plan', parking_spaces: 2, parking_type: 'Attached Garage', exterior_features: 'Patio, Fenced Yard', construction_materials: 'Wood Frame, Stucco', utilities_sewer: 'Public Sewer', utilities_water: 'Public', community_name: 'Westridge Estates', hoa_amenities: 'Pool, Clubhouse, Playground' },
    { heating: 'Oil, Radiant', cooling: 'Wall/Window Unit(s)', appliances: 'Tankless Water Heater, Dishwasher, Microwave, Refrigerator', flooring: 'Hardwood', interior_features: 'Eat-in Kitchen, First Floor Bedroom', parking_spaces: 1, parking_type: 'Off Street, Assigned', exterior_features: 'Courtyard', construction_materials: 'Brick', utilities_sewer: 'Public Sewer', utilities_water: 'Public', community_name: 'Longview Terrace', hoa_amenities: 'Park, Elevator(s)' },
    { heating: 'Electric, Heat Pump', cooling: 'Central Air, Ceiling Fan(s)', appliances: 'Washer, Dryer, Refrigerator, Range', flooring: 'Carpet, Laminate', interior_features: 'Granite Counters, Breakfast Bar', parking_spaces: 2, parking_type: 'Driveway, Garage', exterior_features: 'Deck, Sprinkler System', construction_materials: 'Vinyl Siding', utilities_sewer: 'Septic Tank', utilities_water: 'Well', community_name: null, hoa_amenities: null },
  ];
  const idx = listing.beds ? ((listing.beds as number) % demos.length) : 0;
  return demos[idx];
}

function _factsRow(label: string, value: unknown): string {
  if (!value) return '';
  return `<div class="facts-row"><span class="facts-label">${escapeHtml(label)}</span><span class="facts-value">${escapeHtml(String(value))}</span></div>`;
}

function _detailsCard(icon: string, title: string, summary: string, rowsHtml: string, expanded: boolean): string {
  return `
    <div class="details-card${expanded ? ' expanded' : ''}" onclick="this.classList.toggle('expanded')">
      <div class="details-card-header">
        <div class="details-card-icon"><i data-feather="${icon}" width="16"></i></div>
        <div class="details-card-info">
          <div class="details-card-title">${escapeHtml(title)}</div>
          <div class="details-card-summary">${escapeHtml(summary)}</div>
        </div>
        <i data-feather="chevron-down" width="14" class="details-card-chevron"></i>
      </div>
      <div class="details-card-body">
        ${rowsHtml}
      </div>
    </div>`;
}

function _renderDetailsSection(listing: AnyRecord): string {
  const d = _getDemoExtras(listing);
  const age = listing.year_built ? (new Date().getFullYear() - (listing.year_built as number)) : null;
  // Community & HOA — canonical tri-state from the backend
  // (listing.hoa_exists, mirroring investment_analyzer.hoa_existence):
  // true = HOA exists, false = confirmed none, null = unknown.
  // An explicit boolean wins; otherwise a positive fee implies an HOA
  // exists, an explicit $0 fee implies none. This is the same rule the
  // deal breakdown's operating_expenses.hoa_known follows, so the card
  // and the breakdown can't disagree (e.g. "HOA unknown" on the card
  // while the breakdown shows a measured HOA line).
  const hoaFee = listing.hoa_monthly != null ? Number(listing.hoa_monthly)
    : listing.hoa_fee != null ? Number(listing.hoa_fee) : null;
  const hoaExists: boolean | null = listing.hoa_exists !== undefined && listing.hoa_exists !== null
    ? (listing.hoa_exists as boolean)
    : listing.hoa === true ? true
    : listing.hoa === false ? false
    : hoaFee == null ? null : hoaFee > 0;
  // Tri-state: the card renders whenever the API reported HOA state —
  // an unknown HOA is information worth showing, not a reason to hide
  // the card.
  const hasHoaInfo = listing.hoa_exists !== undefined || listing.hoa !== undefined || d.community_name || d.hoa_amenities;

  let cards = '';

  // Interior — expanded by default
  const interiorSummary = [listing.beds ? listing.beds + ' bd' : '', listing.baths ? listing.baths + ' ba' : '', listing.sqft ? listing.sqft.toLocaleString() + ' sqft' : ''].filter(Boolean).join(' · ');
  cards += _detailsCard('home', 'Interior', interiorSummary, `
    ${_factsRow('Bedrooms', listing.beds)}
    ${_factsRow('Bathrooms', listing.baths)}
    ${listing.sqft ? _factsRow('Living Area', listing.sqft.toLocaleString() + ' sqft') : ''}
    ${_factsRow('Heating', d.heating)}
    ${_factsRow('Cooling', d.cooling)}
    ${_factsRow('Flooring', d.flooring)}
    ${_factsRow('Appliances', d.appliances)}
    ${_factsRow('Features', d.interior_features)}
  `, true);

  // Property
  const propertySummary = [listing.property_type ? listing.property_type.replace(/_/g, ' ') : '', listing.lot_sqft ? listing.lot_sqft.toLocaleString() + ' sqft lot' : ''].filter(Boolean).join(' · ');
  cards += _detailsCard('map-pin', 'Property', propertySummary, `
    ${listing.property_type ? _factsRow('Home Type', listing.property_type.replace(/_/g, ' ')) : ''}
    ${listing.lot_sqft ? _factsRow('Lot Size', listing.lot_sqft.toLocaleString() + ' sqft') : ''}
    ${_factsRow('Parking', (d.parking_spaces || '') + ' space' + (d.parking_spaces !== 1 ? 's' : ''))}
    ${_factsRow('Parking Type', d.parking_type)}
    ${d.exterior_features ? _factsRow('Exterior', d.exterior_features) : ''}
  `, false);

  // Construction
  const constructionSummary = [listing.year_built ? 'Built ' + listing.year_built : '', d.construction_materials].filter(Boolean).join(' · ');
  cards += _detailsCard('tool', 'Construction', constructionSummary, `
    ${listing.year_built ? _factsRow('Year Built', listing.year_built) : ''}
    ${age ? _factsRow('Age', age + ' years') : ''}
    ${_factsRow('Materials', d.construction_materials)}
    ${listing.is_turnkey ? '<div class="facts-row"><span class="facts-label">Move-in Ready</span><span class="facts-value facts-value-good">Yes</span></div>' : ''}
    ${listing.needs_rehab ? '<div class="facts-row"><span class="facts-label">Needs Rehab</span><span class="facts-value facts-value-warn">Yes</span></div>' : ''}
  `, false);

  // Utilities
  cards += _detailsCard('zap', 'Utilities', [d.utilities_sewer, d.utilities_water].filter(Boolean).join(' · '), `
    ${_factsRow('Sewer', d.utilities_sewer)}
    ${_factsRow('Water', d.utilities_water)}
  `, false);

  // Community & HOA
  if (hasHoaInfo) {
    const hoaSummary = hoaExists === true
      ? (hoaFee ? formatPrice(hoaFee) + '/mo' : 'Has HOA')
      : hoaExists === false ? 'No HOA' : 'HOA unknown';
    cards += _detailsCard('users', 'Community & HOA', hoaSummary, `
      ${d.community_name ? _factsRow('Subdivision', d.community_name) : ''}
      <div class="facts-row"><span class="facts-label">HOA</span><span class="facts-value">${hoaExists === true ? (hoaFee ? formatPrice(hoaFee) + '/mo' : 'Yes') : hoaExists === false ? 'None' : 'Unknown'}</span></div>
      ${d.hoa_amenities ? _factsRow('Amenities', d.hoa_amenities) : ''}
    `, false);
  }

  // Financial
  const financialSummary = [formatPrice(listing.price), listing.sqft ? formatPrice(Math.round(listing.price / listing.sqft)) + '/sqft' : ''].filter(Boolean).join(' · ');
  cards += _detailsCard('dollar-sign', 'Financial', financialSummary, `
    <div class="facts-row"><span class="facts-label">List Price</span><span class="facts-value">${formatPrice(listing.price)}</span></div>
    ${listing.sqft ? `<div class="facts-row"><span class="facts-label">Price / sqft</span><span class="facts-value">${formatPrice(Math.round(listing.price / listing.sqft))}</span></div>` : ''}
    ${hoaExists === false && !hasHoaInfo ? '<div class="facts-row"><span class="facts-label">HOA</span><span class="facts-value">None</span></div>' : ''}
    ${hoaExists == null && !hasHoaInfo ? '<div class="facts-row"><span class="facts-label">HOA</span><span class="facts-value">Unknown</span></div>' : ''}
    ${listing.sale_type ? _factsRow('Sale Type', listing.sale_type.replace(/_/g, ' ')) : ''}
    ${_factsRow('Status', listing.status)}
  `, false);

  // Listing Agent
  if (listing.listing_agent_name || listing.listing_agent_brokerage) {
    const agentSummary = [listing.listing_agent_name, listing.listing_agent_brokerage].filter(Boolean).join(' · ');
    cards += _detailsCard('user', 'Listing Agent', agentSummary, `
      ${listing.listing_agent_name ? _factsRow('Agent', listing.listing_agent_name) : ''}
      ${listing.listing_agent_brokerage ? _factsRow('Brokerage', listing.listing_agent_brokerage) : ''}
    `, false);
  }

  let html = `<div class="details-cards">${cards}</div>`;

  // Source link
  if (listing.source_url) {
    html += `<div style="margin-top:12px"><a href="${escapeHtml(listing.source_url)}" target="_blank" rel="noopener" class="facts-source-link"><i data-feather="external-link" width="12"></i> View Original Listing</a></div>`;
  }

  return html;
}


// ── Listing-Agent Activity Feed ─────────────────────
// Per-listing chronological stream of buyer signals. Swipe events
// show anonymized handles (buyer_abc123). The offer inbox +
// offer-related activity events were removed in the Bucket E cleanup.
// Backend endpoint enforces ownership + Fair Housing audit logging.



// ═══════════════════════════════════════════════════════
// Called from inline onclicks in the LA hero (this file) and the
// MarketPulse listing card (trust-layer.ts). Exposed on window via main.ts.
// ═══════════════════════════════════════════════════════

export {
  renderLikes, loadLikes, openDetail, shareListing,
  renderRecentlyViewedRow, clearRecentlyViewed,
  toggleCompareMode, toggleCompareSelection, openCompareScreen, renderCompare,
  renderDetail, galleryGo, galleryNav,
  loadNeighborhood, loadPhotoTruthEnhanced, loadNeighborhoodIQ,
};
